import type { PlayerId } from "@ict/shared";
import { EVENTS, type AppSocket } from "@/network/SocketClient";
import type { PlayerController } from "./PlayerController";
import type { RemotePlayerManager } from "./RemotePlayerManager";

const SEND_RATE_HZ = 15;

/** Throttled outbound movement sync + inbound remote-player event wiring. */
export class NetworkPlayerSync {
  private sequence = 0;
  private sendAccumulator = 0;
  private names = new Map<PlayerId, string>();

  constructor(
    private readonly socket: AppSocket,
    private readonly local: PlayerController,
    private readonly remotes: RemotePlayerManager,
    private readonly myPlayerId: PlayerId
  ) {
    this.socket.on(EVENTS.PLAYER_JOINED, ({ player }) => {
      if (player.playerId === this.myPlayerId) return;
      this.names.set(player.playerId, player.name);
      this.remotes.upsert(player.playerId, player.name, player.position, player.rotationY);
    });
    this.socket.on(EVENTS.PLAYER_MOVED, ({ playerId, position, rotationY }) => {
      if (playerId === this.myPlayerId) return;
      if (!this.names.has(playerId)) {
        this.remotes.upsert(playerId, "PLAYER", position, rotationY);
      }
      this.remotes.applyMove(playerId, position, rotationY);
    });
    this.socket.on(EVENTS.PLAYER_LEFT, ({ playerId }) => {
      this.remotes.remove(playerId);
      this.names.delete(playerId);
    });
  }

  /** Seeds remote avatars for players already in the snapshot at join time. */
  hydrateFromSnapshot(players: { playerId: PlayerId; name: string; position: { x: number; y: number; z: number }; rotationY: number; connected: boolean }[]): void {
    for (const p of players) {
      if (p.playerId === this.myPlayerId || !p.connected) continue;
      this.names.set(p.playerId, p.name);
      this.remotes.upsert(p.playerId, p.name, p.position, p.rotationY);
    }
  }

  update(dt: number): void {
    this.sendAccumulator += dt;
    const interval = 1 / SEND_RATE_HZ;
    if (this.sendAccumulator < interval) return;
    this.sendAccumulator = 0;
    if (!this.local.didMoveThisFrame()) return;

    this.sequence += 1;
    this.socket.emit(EVENTS.PLAYER_MOVE, {
      position: this.local.getPosition(),
      rotationY: this.local.getRotationY(),
      sequence: this.sequence,
    });
  }
}
